/**
 * Barra de controles do mapa (M2) — GUPM-194, fiel ao `.map-controls` do V8.
 * Renderizada ACIMA do mapa, na ordem do V8: tipos de mapa (esquerda) + camadas
 * (direita) em cima, filtro de estrutura (barragem) embaixo. A legenda dos pins
 * (níveis/tipos) fica ABAIXO do mapa (MapLegend).
 */
import type { ReactNode } from 'react';
import { StructureFilter } from '@/ui';
import type { DamOption } from '@/hooks/useDams';
import type { DamSelection } from '@/domain/instrumentos/dam-filter';
import type { StatusEstrutura } from '@/domain/estrutura';
import { BASEMAPS, type BasemapMode } from '../domain/basemaps';
import type { MapLayer } from './MapLegend';
import AssemblyPointIcon from './AssemblyPointIcon';

/** Camadas PAEBM alternáveis (mesmos ids do estado de camadas do M2). */
const LAYER_CHIP: { layer: MapLayer; label: string; dot: string; icon?: ReactNode }[] = [
  { layer: 'mancha', label: 'Mancha (ZAS)', dot: '#ef4444' },
  { layer: 'zss', label: '🟧 ZSS', dot: '#f97316' },
  { layer: 'sirenes', label: '📢 Sirenes', dot: '#22c55e' },
  { layer: 'edificacoes', label: '🏠 Edificações ZAS', dot: '#f97316' },
  {
    layer: 'pontosEncontro',
    label: 'Pontos de encontro',
    dot: '#16a34a',
    // Um único ícone (ISO 7010) sinaliza TODOS os PEs na legenda; a distinção
    // interno/externo por cor fica só nos marcadores do mapa (pedido do dono).
    icon: <AssemblyPointIcon size={13} color="#16a34a" />,
  },
];

interface Props {
  dams: DamOption[];
  damCounts: Record<string, number>;
  selectedDam: DamSelection;
  onSelectDam: (d: DamSelection) => void;
  /** Status consolidado por `damId` (mesma fonte do header) — colore as pills. */
  statusByDam?: Record<string, StatusEstrutura>;
  basemap: BasemapMode;
  onBasemap: (m: BasemapMode) => void;
  layers: Record<MapLayer, boolean>;
  onToggleLayer: (l: MapLayer) => void;
}

function typePillClass(active: boolean): string {
  return `inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold transition ${
    active ? 'bg-jf-yellow text-jf-bg' : 'border border-jf-border text-white hover:text-jf-yellow'
  }`;
}

function layerChipClass(active: boolean): string {
  return `inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] transition ${
    active ? 'border-jf-border bg-jf-surface2 text-white' : 'border-jf-border/50 text-jf-dim opacity-50 line-through'
  }`;
}

export default function MapControls({
  dams, damCounts, selectedDam, onSelectDam, statusByDam, basemap, onBasemap, layers, onToggleLayer,
}: Props) {
  return (
    <div className="mb-2 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="group" aria-label="Tipo de mapa" className="flex flex-wrap gap-1">
          {BASEMAPS.map((b) => (
            <button
              key={b.id}
              type="button"
              aria-pressed={basemap === b.id}
              onClick={() => onBasemap(b.id)}
              className={typePillClass(basemap === b.id)}
            >
              {b.label}
            </button>
          ))}
        </div>
        <div role="group" aria-label="Camadas" className="flex flex-wrap gap-1">
          {LAYER_CHIP.map(({ layer, label, dot, icon }) => (
            <button
              key={layer}
              type="button"
              aria-pressed={layers[layer]}
              onClick={() => onToggleLayer(layer)}
              className={layerChipClass(layers[layer])}
            >
              {icon ?? (
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: dot }} />
              )}
              {label}
            </button>
          ))}
        </div>
      </div>
      <StructureFilter
        dams={dams}
        counts={damCounts}
        selected={selectedDam}
        onSelect={onSelectDam}
        statusByDam={statusByDam}
        size="sm"
      />
    </div>
  );
}
